import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from 'react-i18next';
import { updateSettings } from '../../store/reducers/AccountSlice';
import PrimaryButton from "../PrimaryButton/PrimaryButton";
import SecondaryButton from "../SecondaryButton/SecondaryButton";
import { CardV2, CardContent } from '../CardV2/CardV2';
import Title from "../Title/Title";
import { CounterInput } from '../CounterInput/CounterInput';
import { InputV2 } from '../InputV2/InputV2';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { MeetingType } from '../../shared/meetingTypeToTranslationKey';
import { Check } from "lucide-react";
import { toastService } from '../../services/ToastService';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import BookingLinkInput from '../BookingLinkInput/BookingLinkInput';
import PlainTextButton from '../PlainTextButton/PlainTextButton';
import BookingPlansAPI, { BookingPlan } from '../../api/BookingPlansAPI';
import CopyText from '../CopyText/CopyText';
import ConfigAPI from '../../api/ConfigAPI';

type Weekday = "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday";

interface PurposeOption {
  value: MeetingType;
  label: string;
  description: string;
}

interface DayHours {
  enabled: boolean;
  opening: string;
  closing: string;
}

const getPurposeOptions = (t: any): PurposeOption[] => [
  { value: 'VISIT', label: t('onboarding.booking.step2.purpose.visit.label'), description: t('onboarding.booking.step2.purpose.visit.description') },
  { value: 'FIRST_MEASUREMENT', label: t('onboarding.booking.step2.purpose.first_measurement.label'), description: t('onboarding.booking.step2.purpose.first_measurement.description') },
  { value: 'CONSULTING_SERVICES', label: t('onboarding.booking.step2.purpose.consulting_services.label'), description: t('onboarding.booking.step2.purpose.consulting_services.description') },
  { value: 'OTHER', label: t('onboarding.booking.step2.purpose.other.label'), description: t('onboarding.booking.step2.purpose.other.description') },
];

const WEEKDAYS: Weekday[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const OnboardingBookingFlow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/";
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const settings = useSelector((state: any) => state.settings.value);
  const brandName = settings?.tenantName;
  
  const [stepIndex, setStepIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [loading, setLoading] = useState(false);

  const languageSwitcher = <LanguageSwitcher className="mt-2" />;
  
  const PURPOSE_OPTIONS = useMemo(() => getPurposeOptions(t), [t]);
  const bookingNameHasBeenEdited = useRef(false);
  const previousPlaceholder = useRef<string>(t('onboarding.booking.step1.placeholder') as string);
  const customLinkTextHasBeenEdited = useRef(false);
  const [bookingName, setBookingName] = useState(t('onboarding.booking.step1.placeholder') as string);
  const [selectedPurpose, setSelectedPurpose] = useState<MeetingType>('VISIT');
  const [duration, setDuration] = useState(60);
  const [selectedDays, setSelectedDays] = useState<Weekday[]>(['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']);
  const [dayHours, setDayHours] = useState<Record<Weekday, DayHours>>({
    Monday: { enabled: true, opening: "09:00", closing: "17:00" },
    Tuesday: { enabled: true, opening: "09:00", closing: "17:00" },
    Wednesday: { enabled: true, opening: "09:00", closing: "17:00" },
    Thursday: { enabled: true, opening: "09:00", closing: "17:00" },
    Friday: { enabled: true, opening: "09:00", closing: "17:00" },
    Saturday: { enabled: false, opening: "10:00", closing: "16:00" },
    Sunday: { enabled: false, opening: "", closing: "" },
  });
  const [autoConfirm, setAutoConfirm] = useState(false);
  const [fittingRooms, setFittingRooms] = useState(1);
  const [customLinkText, setCustomLinkText] = useState("");
  const [finalBookingLink, setFinalBookingLink] = useState("");
  const [isLinkValid, setIsLinkValid] = useState(false);

  useEffect(() => {
    // Only auto-generate on initial load if the field hasn't been manually edited
    if (brandName && !customLinkText && !customLinkTextHasBeenEdited.current) {
      const sanitized = brandName.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      setCustomLinkText(sanitized);
    }
  }, [brandName, customLinkText]);

  useEffect(() => {
    if (customLinkText) {
      setFinalBookingLink(`${window.location.origin}/bookings/${customLinkText}`);
    }
  }, [customLinkText]);

  useEffect(() => {
    const currentPlaceholder = t('onboarding.booking.step1.placeholder') as string;
    if (!bookingNameHasBeenEdited.current || bookingName === previousPlaceholder.current) {
      setBookingName(currentPlaceholder);
    }
    previousPlaceholder.current = currentPlaceholder;
  }, [i18n.language, t, bookingName]);

  const totalSteps = 8;
  const isLastStep = stepIndex === totalSteps - 1;

  const handleDayToggle = (day: Weekday) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
      // Set default hours if not already set
      if (!dayHours[day].opening) {
        setDayHours({
          ...dayHours,
          [day]: { enabled: true, opening: "09:00", closing: "17:00" }
        });
      }
    }
  };

  const handleDayHoursChange = (day: Weekday, field: 'opening' | 'closing', value: string) => {
    setDayHours({
      ...dayHours,
      [day]: {
        ...dayHours[day],
        [field]: value
      }
    });
  };

  const validateTimeFormat = (time: string) => {
    const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return timeRegex.test(time);
  };

  const validateDayHours = (day: Weekday): boolean => {
    const hours = dayHours[day];
    if (!selectedDays.includes(day)) return true;
    
    if (!validateTimeFormat(hours.opening) || !validateTimeFormat(hours.closing)) {
      return false;
    }
    
    const [openingHour, openingMinute] = hours.opening.split(':').map(Number);
    const [closingHour, closingMinute] = hours.closing.split(':').map(Number);
    
    return closingHour > openingHour || (closingHour === openingHour && closingMinute > openingMinute);
  };

  const canProceed = () => {
    if (stepIndex === 0) return true; // Intro
    if (stepIndex === 1) return bookingName && bookingName.trim().length > 0; // Name
    if (stepIndex === 2) return selectedPurpose !== null; // Purpose
    if (stepIndex === 3) return duration > 0; // Duration
    if (stepIndex === 4) {
      // Days and hours
      if (selectedDays.length === 0) return false;
      return selectedDays.every(day => validateDayHours(day));
    }
    if (stepIndex === 5) return true; // Auto-confirm (always valid)
    if (stepIndex === 6) return fittingRooms > 0; // Fitting rooms
    if (stepIndex === 7) return isLinkValid && customLinkText.trim().length > 0; // Link
    return true;
  };

  const handleSkip = () => {
    navigate(redirectTo);
  };

  const handleNext = () => {
    if (!canProceed()) return;
    
    if (isLastStep) {
      handleComplete();
    } else {
      setFade(false);
      setTimeout(() => {
        setStepIndex((prev) => prev + 1);
        setFade(true);
      }, 300);
    }
  };

  const handleBack = () => {
    if (stepIndex > 0) {
      setFade(false);
      setTimeout(() => {
        setStepIndex((prev) => prev - 1);
        setFade(true);
      }, 300);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      if (customLinkText) {
        await BookingPlansAPI.setBookingLink(customLinkText);
        dispatch(updateSettings({
          bookingWidgetId: customLinkText
        }));
      }

      const weekdayHours: Record<string, { enabled: boolean; opening: string; closing: string }> = {};
      WEEKDAYS.forEach((day) => {
        weekdayHours[day] = {
          enabled: selectedDays.includes(day),
          opening: dayHours[day].opening || "09:00",
          closing: dayHours[day].closing || "17:00",
        };
      });

      const bookingPlan: BookingPlan = {
        name: bookingName || t('onboarding.booking.step1.placeholder') as string,
        internalType: selectedPurpose,
        customerInfo: "",
        automaticallyAccept: autoConfirm,
        requiresFee: false,
        enabled: true,
        duration: `${duration}`,
        useCalendarAvailability: false,
        weekdayHours: weekdayHours,
        customerData: {
          requestEmail: true,
          requestPhone: true,
          requestName: true,
          requestBudget: false,
          requestSize: false,
          requestWeddingDate: false,
          requestNumberOfPeople: false,
          allowSpecialComments: true,
          requestWhereHeardFrom: true,
        },
        budgetRanges: [],
        requiresRefundableFee: false,
        useAdditionalRestrictions: false,
        useOnlinePayments: false,
        feeIsDeposit: false,
        brideCanCancel: false,
      };

      await BookingPlansAPI.create(bookingPlan);

      try {
        const currentConfig = await ConfigAPI.get();
        await ConfigAPI.update({
          ...currentConfig.data,
          fittingRooms: fittingRooms,
          bookingLink: customLinkText || currentConfig.data.bookingLink
        });
      } catch (configError) {
        console.error('Error updating config with fitting rooms:', configError);
      }

      setFade(false);
      setTimeout(() => {
        setStepIndex((prev) => prev + 1);
        setFade(true);
        setLoading(false);
      }, 300);
    } catch (error: any) {
      console.error('Error creating booking plan:', error);
      toastService.showToast(
        error?.response?.data?.message || t('onboarding.booking.error'),
        'error'
      );
      setLoading(false);
    }
  };


  const handleLinkTextChange = (value: string) => {
    customLinkTextHasBeenEdited.current = true;
    setCustomLinkText(value);
  };

  const renderStep = () => {
    switch (stepIndex) {
      case 0:
        return (
          <div className="flex flex-col items-center space-y-8">
            <div className="text-center mb-4">
              <Title 
                text={t('onboarding.booking.title') as string} 
                clazz="text-3xl font-bold text-darker-gold-default mb-4 text-center" 
              />
              <div className="h-1 w-32 bg-gradient-to-r from-darker-gold-300 to-as-pink-300 rounded-full mx-auto" />
            </div>
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.intro.description')}
            </p>
            <PrimaryButton
              text={t('onboarding.booking.intro.button') as string}
              fn={handleNext}
              actionName="onboarding_booking_start"
            />
            {languageSwitcher}
          </div>
        );

      case 1:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.step1.question')}
            </p>
            <div className="w-full max-w-md">
              <InputV2
                type="text"
                value={bookingName}
                onChange={(e) => {
                  bookingNameHasBeenEdited.current = true;
                  setBookingName(e.target.value);
                }}
                placeholder={t('onboarding.booking.step1.placeholder') as string}
                className="w-full"
              />
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton
                  text={t('onboarding.booking.back') as string}
                  fn={handleBack}
                  actionName="onboarding_booking_back"
                />
                <PrimaryButton
                  text={t('onboarding.booking.continue') as string}
                  fn={handleNext}
                  actionName="onboarding_booking_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton
                text={t('onboarding.booking.skip') as string}
                onClick={handleSkip}
                actionName="onboarding_booking_skip"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      case 2:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.step2.question')}
            </p>
            <div className="w-full max-w-2xl space-y-3">
              {PURPOSE_OPTIONS.map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group ${
                    selectedPurpose === option.value
                      ? 'border-darker-gold-300 bg-darker-gold-50'
                      : 'border-gray-200 hover:border-darker-gold-200 hover:bg-darker-gold-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="purpose"
                    value={option.value}
                    checked={selectedPurpose === option.value}
                    onChange={() => setSelectedPurpose(option.value)}
                    className="mt-1 w-5 h-5 accent-darker-gold-300 border-gray-300 focus:ring-darker-gold-200 focus:ring-2 cursor-pointer"
                  />
                  <div className="ml-3 flex-1">
                    <span className="text-gray-900 font-medium group-hover:text-darker-gold-DEFAULT transition-colors duration-200">
                      {option.label}
                    </span>
                    <p className="text-sm text-gray-500 mt-1">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton
                  text={t('onboarding.booking.back') as string}
                  fn={handleBack}
                  actionName="onboarding_booking_back"
                />
                <PrimaryButton
                  text={t('onboarding.booking.continue') as string}
                  fn={handleNext}
                  actionName="onboarding_booking_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton
                text={t('onboarding.booking.skip') as string}
                onClick={handleSkip}
                actionName="onboarding_booking_skip"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      case 3:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.step3.question')}
            </p>
            <div className="w-full max-w-md flex flex-col items-center">
              <CounterInput
                value={duration}
                onChange={setDuration}
              />
              <p className="text-sm text-gray-500 mt-2 text-center">
                {duration} {duration === 1 ? t('onboarding.booking.step3.minute') : t('onboarding.booking.step3.minutes')}
              </p>
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton
                  text={t('onboarding.booking.back') as string}
                  fn={handleBack}
                  actionName="onboarding_booking_back"
                />
                <PrimaryButton
                  text={t('onboarding.booking.continue') as string}
                  fn={handleNext}
                  actionName="onboarding_booking_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton
                text={t('onboarding.booking.skip') as string}
                onClick={handleSkip}
                actionName="onboarding_booking_skip"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      case 4:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.step4.question')}
            </p>
            <div className="w-full max-w-2xl space-y-4">
              {/* Day selection */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {WEEKDAYS.map((day) => (
                  <label
                    key={day}
                    className={`flex items-center p-3 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                      selectedDays.includes(day)
                        ? 'border-darker-gold-300 bg-darker-gold-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDays.includes(day)}
                      onChange={() => handleDayToggle(day)}
                      className="w-4 h-4 accent-darker-gold-300 border-gray-300 rounded focus:ring-darker-gold-200 cursor-pointer"
                    />
                    <span className="ml-2 text-gray-700 font-medium">{t(`global.${day.toLowerCase()}`)}</span>
                  </label>
                ))}
              </div>

              {/* Time inputs for selected days */}
              {selectedDays.length > 0 && (
                <div className="space-y-4 pt-4 border-t">
                  <p className="text-sm font-medium text-gray-700">{t('onboarding.booking.step4.opening_hours')}</p>
                  {selectedDays.map((day) => (
                    <div key={day} className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-700 min-w-[100px]">{t(`global.${day.toLowerCase()}`)}:</div>
                      <div className="flex-1 grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('onboarding.booking.step4.opening')}</label>
                          <InputV2
                            type="time"
                            value={dayHours[day].opening}
                            onChange={(e) => handleDayHoursChange(day, 'opening', e.target.value)}
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500 mb-1 block">{t('onboarding.booking.step4.closing')}</label>
                          <InputV2
                            type="time"
                            value={dayHours[day].closing}
                            onChange={(e) => handleDayHoursChange(day, 'closing', e.target.value)}
                            className="w-full"
                          />
                        </div>
                      </div>
                      {!validateDayHours(day) && (
                        <p className="text-red-500 text-xs col-span-2">
                          {t('onboarding.booking.step4.time_validation_error')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton
                  text={t('onboarding.booking.back') as string}
                  fn={handleBack}
                  actionName="onboarding_booking_back"
                />
                <PrimaryButton
                  text={t('onboarding.booking.continue') as string}
                  fn={handleNext}
                  actionName="onboarding_booking_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton
                text={t('onboarding.booking.skip') as string}
                onClick={handleSkip}
                actionName="onboarding_booking_skip"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      case 5:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.step5.question')}
            </p>
            <div className="w-full max-w-2xl space-y-3">
              <label
                className={`flex items-start p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group ${
                  !autoConfirm
                    ? 'border-darker-gold-300 bg-darker-gold-50'
                    : 'border-gray-200 hover:border-darker-gold-200 hover:bg-darker-gold-50'
                }`}
              >
                <input
                  type="radio"
                  name="confirm"
                  checked={!autoConfirm}
                  onChange={() => setAutoConfirm(false)}
                  className="mt-1 w-5 h-5 accent-darker-gold-300 border-gray-300 focus:ring-darker-gold-200 focus:ring-2 cursor-pointer"
                />
                <div className="ml-3">
                  <span className="text-gray-900 font-medium">{t('onboarding.booking.step5.manual.label')}</span>
                  <p className="text-sm text-gray-500 mt-1">{t('onboarding.booking.step5.manual.description')}</p>
                </div>
              </label>
              <label
                className={`flex items-start p-4 rounded-xl border-2 transition-all duration-200 cursor-pointer group ${
                  autoConfirm
                    ? 'border-darker-gold-300 bg-darker-gold-50'
                    : 'border-gray-200 hover:border-darker-gold-200 hover:bg-darker-gold-50'
                }`}
              >
                <input
                  type="radio"
                  name="confirm"
                  checked={autoConfirm}
                  onChange={() => setAutoConfirm(true)}
                  className="mt-1 w-5 h-5 accent-darker-gold-300 border-gray-300 focus:ring-darker-gold-200 focus:ring-2 cursor-pointer"
                />
                <div className="ml-3">
                  <span className="text-gray-900 font-medium">{t('onboarding.booking.step5.automatic.label')}</span>
                  <p className="text-sm text-gray-500 mt-1">{t('onboarding.booking.step5.automatic.description')}</p>
                </div>
              </label>
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton
                  text={t('onboarding.booking.back') as string}
                  fn={handleBack}
                  actionName="onboarding_booking_back"
                />
                <PrimaryButton
                  text={t('onboarding.booking.continue') as string}
                  fn={handleNext}
                  actionName="onboarding_booking_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton
                text={t('onboarding.booking.skip') as string}
                onClick={handleSkip}
                actionName="onboarding_booking_skip"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      case 6:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.step6.question')}
            </p>
            <div className="w-full max-w-md space-y-4">
              <div className="flex items-center justify-center space-x-3">
                <ButtonV2
                  variant="outline"
                  size="icon"
                  onClick={() => setFittingRooms(Math.max(1, fittingRooms - 1))}
                >
                  -
                </ButtonV2>
                <InputV2
                  type="number"
                  min="1"
                  value={fittingRooms}
                  onChange={(e) => setFittingRooms(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-20 text-center"
                />
                <ButtonV2
                  variant="outline"
                  size="icon"
                  onClick={() => setFittingRooms(fittingRooms + 1)}
                >
                  +
                </ButtonV2>
              </div>
              <p className="text-sm text-gray-600 text-center max-w-md">
                {t('onboarding.booking.step6.description')}
              </p>
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton
                  text={t('onboarding.booking.back') as string}
                  fn={handleBack}
                  actionName="onboarding_booking_back"
                />
                <PrimaryButton
                  text={t('onboarding.booking.continue') as string}
                  fn={handleNext}
                  actionName="onboarding_booking_next"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton
                text={t('onboarding.booking.skip') as string}
                onClick={handleSkip}
                actionName="onboarding_booking_skip"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      case 7:
        return (
          <div className="flex flex-col items-center space-y-8">
            <p className="text-lg text-gray-700 leading-relaxed max-w-2xl text-center">
              {t('onboarding.booking.step7.question')}
            </p>
            <div className="w-full max-w-2xl space-y-4">
              <BookingLinkInput
                value={customLinkText}
                onChange={handleLinkTextChange}
                placeholder={brandName.toLowerCase().replace(/[^a-z0-9]/g, '-')}
                onValidationChange={setIsLinkValid}
              />
            </div>
            <div className="flex flex-col items-center gap-4 w-full max-w-md">
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center">
                <SecondaryButton
                  text={t('onboarding.booking.back') as string}
                  fn={handleBack}
                  actionName="onboarding_booking_back"
                />
                <PrimaryButton
                  text={t('onboarding.booking.step7.button') as string}
                  fn={handleNext}
                  actionName="onboarding_booking_complete"
                  isDisabled={!canProceed()}
                />
              </div>
              <PlainTextButton
                text={t('onboarding.booking.skip') as string}
                onClick={handleSkip}
                actionName="onboarding_booking_skip"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      case 8:
        return (
          <div className="flex flex-col items-center space-y-8">
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <Title 
                text={t('onboarding.booking.final.title') as string} 
                clazz="text-3xl font-bold text-darker-gold-default mb-4 text-center" 
              />
              <p className="text-lg text-gray-700 leading-relaxed max-w-2xl">
                {t('onboarding.booking.final.description')}
              </p>
            </div>

            {/* Booking Link */}
            <div className="w-full max-w-2xl bg-gray-50 rounded-lg p-6 space-y-4">
              <div className="flex flex-wrap items-center gap-2 bg-white p-3 rounded-md border border-gray-200">
                <span className="text-gray-500 truncate flex-1 min-w-0">
                  {finalBookingLink}
                </span>
                <CopyText text={finalBookingLink} label={t('onboarding.booking.final.copy_link') as string} />
              </div>

              {/* Suggestions */}
              <div className="space-y-4 pt-4 border-t border-gray-200">
                <p className="font-medium text-gray-900">{t('onboarding.booking.final.next_steps.title')}</p>
                <div className="space-y-3">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-darker-gold-100 flex items-center justify-center">
                      <span className="text-darker-gold-600 font-semibold text-sm">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{t('onboarding.booking.final.next_steps.test.title')}</p>
                      <p className="text-sm text-gray-600">{t('onboarding.booking.final.next_steps.test.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-darker-gold-100 flex items-center justify-center">
                      <span className="text-darker-gold-600 font-semibold text-sm">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{t('onboarding.booking.final.next_steps.instagram.title')}</p>
                      <p className="text-sm text-gray-600">{t('onboarding.booking.final.next_steps.instagram.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-darker-gold-100 flex items-center justify-center">
                      <span className="text-darker-gold-600 font-semibold text-sm">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{t('onboarding.booking.final.next_steps.stories.title')}</p>
                      <p className="text-sm text-gray-600">{t('onboarding.booking.final.next_steps.stories.description')}</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-darker-gold-100 flex items-center justify-center">
                      <span className="text-darker-gold-600 font-semibold text-sm">4</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{t('onboarding.booking.final.next_steps.website.title')}</p>
                      <p className="text-sm text-gray-600">{t('onboarding.booking.final.next_steps.website.description')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center">
              <PrimaryButton
                text={t('onboarding.booking.final.button') as string}
                fn={() => navigate('/bookings')}
                actionName="onboarding_booking_finish"
              />
            </div>
            {languageSwitcher}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-darker-gold-50 via-white to-as-pink-100">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-darker-gold-200 rounded-full opacity-10 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-as-pink-300 rounded-full opacity-10 blur-3xl" />
      </div>

      <CardV2 className="w-full max-w-4xl shadow-2xl border-0 bg-white/95 backdrop-blur-sm rounded-3xl relative z-10">
        <CardContent className="p-8">
          <div className={`transition-all duration-500 ease-in-out ${fade ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            {/* Progress indicator */}
            {stepIndex > 0 && stepIndex < totalSteps - 1 && (
              <div className="flex justify-center mb-8">
                <div className="flex space-x-2">
                  {Array.from({ length: 7 }).map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 w-8 rounded-full transition-all duration-300 ${
                        index < stepIndex 
                          ? 'bg-darker-gold-300 shadow-lg' 
                          : 'bg-gray-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Title */}
            {stepIndex > 0 && stepIndex < totalSteps - 1 && (
              <div className="text-center mb-8">
                <Title 
                  text={t('onboarding.booking.title') as string} 
                  clazz="text-3xl font-bold text-darker-gold-default mb-4 text-center" 
                />
                <div className="h-1 w-32 bg-gradient-to-r from-darker-gold-300 to-as-pink-300 rounded-full mx-auto" />
              </div>
            )}

            {/* Loading state */}
            {loading && (
              <div className="text-center text-darker-gold-300 mb-6 font-medium">
                {t('onboarding.booking.loading')}
              </div>
            )}

            {/* Step content */}
            {renderStep()}
          </div>
        </CardContent>
      </CardV2>
    </div>
  );
};

export default OnboardingBookingFlow;

